import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';

import Button from 'modules/_shared/components/Buttons';
import { setModal } from 'modules/_shared/redux/modalActions';
import { Council, Document } from 'types';
import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { updateCouncil } from 'modules/administratives/redux/councilActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import { addDocument } from 'modules/documents/redux/documentActions';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import { ScreenSizes } from 'constants/sizes';
import UploadDocument from 'modules/documents/components/UploadDocument';
import useGetDocumentData from 'modules/administratives/pages/CouncilDetails/hooks/useGetDocumentData';
import PDFAdobeViewer from 'modules/_shared/components/PDFAdobeViewer';
import councilTemplateTypes from 'modules/administratives/pages/CouncilDetails/constants/councilTemplateTypes';
import signatureTypes from 'constants/signatureTypes';
import RequestSignatureModal from '../RequestSignatureModal';

type EditCouncilActCertificateProps = {
  size?: ScreenSizes;
  council: Council;
};

const EditCouncilActCertificate: FC<EditCouncilActCertificateProps> = ({
  council,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state.user);
  const society = useSelector((state: State) => state.society.actualSociety);

  const [isLoading, setIsLoading] = useState(false);
  const [actFile, setActFile] = useState<File | undefined>(undefined);
  const [certificateFile, setCertificateFile] = useState<File | undefined>(
    undefined
  );

  const actTemplateData = useGetDocumentData({
    documentId: council.actDocument ?? null,
    templateId: council.actTemplate ?? null,
  });

  const certificateTemplateData = useGetDocumentData({
    documentId: council.actCertificateDocument ?? null,
    templateId: council.actCertificateTemplate ?? null,
  });

  const { documentFrame: actDocumentFrame, templateName: actTemplateName } =
    actTemplateData.data;
  const {
    documentFrame: certificateDocumentFrame,
    templateName: certificateTemplateName,
  } = certificateTemplateData.data;
  const { isLoading: isLoadingActTemplate } = actTemplateData;
  const { isLoading: isLoadingCertificateTemplate } = certificateTemplateData;

  const handleClose = () => {
    dispatch(setModal(null));
  };

  const renderActPreview = () => {
    if (isLoadingActTemplate) {
      return (
        <div className="text-center text-muted p-4">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">{t('Loading...')}</span>
          </div>
          {t('Loading template...')}
        </div>
      );
    }

    if (actFile) {
      return (
        <iframe
          src={URL.createObjectURL(actFile)}
          width="100%"
          height="400"
          style={{
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
          title="Act Preview"
        />
      );
    }

    if (actDocumentFrame) {
      return (
        <PDFAdobeViewer
          fileUrl={actDocumentFrame}
          fileName={actTemplateName || 'Act Template'}
          height="400px"
          className="border rounded"
        />
      );
    }

    return (
      <div className="text-center text-muted p-4">
        {t('No template available')}
      </div>
    );
  };

  const renderCertificatePreview = () => {
    if (isLoadingCertificateTemplate) {
      return (
        <div className="text-center text-muted p-4">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">{t('Loading...')}</span>
          </div>
          {t('Loading template...')}
        </div>
      );
    }

    if (certificateFile) {
      return (
        <iframe
          src={URL.createObjectURL(certificateFile)}
          width="100%"
          height="400"
          style={{
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
          title="Certificate Preview"
        />
      );
    }

    if (certificateDocumentFrame) {
      return (
        <PDFAdobeViewer
          fileUrl={certificateDocumentFrame}
          fileName={certificateTemplateName || 'Certificate Template'}
          height="400px"
          className="border rounded"
        />
      );
    }

    return (
      <div className="text-center text-muted p-4">
        {t('No template available')}
      </div>
    );
  };

  const uploadDocument = async (
    file: File,
    documentName: string,
    description: string
  ): Promise<Document | null> => {
    try {
      const newDocument = {
        file,
        date: new Date(),
        size: file.size,
        name: documentName,
        author: user?._id,
        society: society?._id,
        fileType: fileTypes[file.type as keyof typeof fileTypes],
        category: documentTypes.DOCUMENTS,
        subcategory: 'COUNCIL',
        description,
      };

      const uploadedDocument = await store.dispatch(addDocument(newDocument));
      return uploadedDocument;
    } catch (error) {
      return null;
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const newCouncilData: Partial<Council> = {
        _id: council._id,
      };

      // Upload act document if file is selected
      if (actFile) {
        const uploadedActDocument = await uploadDocument(
          actFile,
          `Act-${council.name}-${new Date().toISOString().split('T')[0]}.pdf`,
          t('Council Act Document')
        );
        if (uploadedActDocument) {
          newCouncilData.actDocument = uploadedActDocument._id;
        }
      }

      // Upload certificate document if file is selected
      if (certificateFile) {
        const uploadedCertificateDocument = await uploadDocument(
          certificateFile,
          `Certificate-${council.name}-${
            new Date().toISOString().split('T')[0]
          }.pdf`,
          t('Council Certificate Document')
        );
        if (uploadedCertificateDocument) {
          newCouncilData.actCertificateDocument =
            uploadedCertificateDocument._id;
        }
      }

      // Only update if there are changes
      if (actFile || certificateFile) {
        await store.dispatch(updateCouncil(newCouncilData));
        await store.dispatch(addAlert(alertBodyTypes.COUNCIL_UPDATED));
      }

      dispatch(setModal(null));
    } catch (error) {
      await store.dispatch(addAlert(alertBodyTypes.ERROR_UPDATING_COUNCIL));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestActSignature = () => {
    if (!council._id || !actTemplateData.data.document) {
      return;
    }

    dispatch(
      setModal(
        <RequestSignatureModal
          councilId={council._id}
          document={actTemplateData.data.document}
          templateType={councilTemplateTypes.ACT}
          signatureType={signatureTypes.COUNCIL_ACT}
        />
      )
    );
  };

  const handleRequestCertificateSignature = () => {
    if (!council._id || !certificateTemplateData.data.document) {
      return;
    }

    dispatch(
      setModal(
        <RequestSignatureModal
          councilId={council._id}
          document={certificateTemplateData.data.document}
          templateType={councilTemplateTypes.CERTIFICATION}
          signatureType={signatureTypes.COUNCIL_CERTIFICATE}
        />
      )
    );
  };

  return (
    <>
      <Modal.Header>
        <h4 className="modal-title mb-0">
          <span>{t('Edit Act & Certificate')}</span>
        </h4>

        <a className="close cursor-pointer" onClick={handleClose}>
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body className="custom-modal-body">
        <div className="nk-modal gy-4">
          <div className="row">
            {/* Left side - Act */}
            <div className="col-md-6">
              <div className="card">
                <div className="card-body">
                  <p className="text-muted mb-3">
                    {t(
                      'By default you have the default Act template, but you can select a custom file to override it'
                    )}
                  </p>

                  <div className="mb-3">
                    <UploadDocument
                      label="Select Act File"
                      file={actFile}
                      setFile={setActFile}
                      idIndex={1}
                    />
                  </div>

                  {/* Show template preview or uploaded file preview */}
                  <div className="mt-3">
                    <h6>{t('Preview')}</h6>
                    {renderActPreview()}
                  </div>

                  <div className="mt-3">
                    <Button
                      className="btn btn-block"
                      icon="ni ni-edit-alt"
                      onClick={handleRequestActSignature}
                    >
                      {t('RequestSignature')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Certificate */}
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">{t('Certificate')}</h5>
                </div>
                <div className="card-body">
                  <p className="text-muted mb-3">
                    {t(
                      'By default you have the default Certificate template, but you can select a custom file to override it'
                    )}
                  </p>

                  <div className="mb-3">
                    <UploadDocument
                      label="Select Certificate File"
                      file={certificateFile}
                      setFile={setCertificateFile}
                      idIndex={2}
                    />
                  </div>

                  {/* Show template preview or uploaded file preview */}
                  <div className="mt-3">
                    <h6>{t('Preview')}</h6>
                    {renderCertificatePreview()}
                  </div>

                  <div className="mt-3">
                    <Button
                      className="btn btn-block"
                      icon="ni ni-edit-alt"
                      onClick={handleRequestCertificateSignature}
                    >
                      {t('RequestSignature')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isLoading && <CustomLoading />}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={(!actFile && !certificateFile) || isLoading}
        >
          {isLoading ? t('Saving...') : t('Save')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default EditCouncilActCertificate;
